import { writeParagraphsToClipboard, writeTextToClipboard } from '../utils';
import NavigationUtils from '../utils/NavigationUtils';
import ResourceUtils from '../utils/ResourceUtils';

/**
 * History of items copied by the user (Ctrl/Cmd+C and "copy" buttons).
 *
 * Items are stored in the browser localStorage, isolated per user, with a 24h
 * expiry and a maximum of 50 entries. The list is shown in a dropdown in the
 * top navbar.
 */
export default class ClipboardHistory {
	static MAX_ITEMS = 50;
	static TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
	static PREVIEW_LEN = 120;

	#seq = 0;
	#reopenDropdownAfterOverlay = false;
	#skipNextRender = false;

	constructor() {
		const userId = window.authconfig?.user?.id ?? 'anon';
		this.storageKey = `naxos-clipboard-${userId}`;
		this.listEl = null;
		this.menuEl = null;
	}

	/**
	 * Reads the list from storage, removing expired entries and normalizing the
	 * format. Each entry is an object { id, original, value, ts }:
	 * - `value` is the actually pasteable text (e.g. the translation);
	 * - `original` is the source text (empty for plain copies).
	 * Old entries saved as { text } are migrated to { value }.
	 */
	#read() {
		let raw;
		try {
			raw = JSON.parse(localStorage.getItem(this.storageKey)) || [];
		} catch (_e) {
			raw = [];
		}
		if (!Array.isArray(raw)) return [];

		const now = Date.now();
		const valid = raw
			.map((it) => {
				if (!it || typeof it.ts !== 'number') return null;
				const value = typeof it.value === 'string' ? it.value : typeof it.text === 'string' ? it.text : null;
				if (value === null) return null;
				return {
					id: it.id,
					original: typeof it.original === 'string' ? it.original : '',
					value,
					ts: it.ts,
				};
			})
			.filter((it) => it && now - it.ts < ClipboardHistory.TTL_MS);

		// If we dropped expired entries, rewrite storage.
		if (valid.length !== raw.length) {
			this.#persist(valid);
		}
		return valid;
	}

	#persist(items) {
		try {
			localStorage.setItem(this.storageKey, JSON.stringify(items));
		} catch (_e) {
			// storage full or unavailable: ignore
		}
	}

	/**
	 * Adds an entry to the top of the list (dedup + cap + expiry).
	 *
	 * Accepts a string (plain copy) or an object { original, value } (e.g. a
	 * translation, where `original` is the source text and `value` is the
	 * actually pasteable translation).
	 */
	add(input) {
		let original = '';
		let value = '';
		if (input && typeof input === 'object') {
			original = this.#normalizeValue(input.original);
			value = this.#normalizeValue(input.value);
		} else {
			value = this.#normalizeValue(input);
		}
		if (!value) return;

		// Inside an iframe/popup (e.g. the translation panel): the top window owns
		// the history (storage + navbar dropdown). Forward the copy to it so the
		// main window updates live, without needing a reload.
		if (window.top !== window.self) {
			try {
				window.top.postMessage({ action: 'clipboard-add', original, value }, window.location.origin);
			} catch (_e) {
				// cross-origin top or postMessage unavailable: ignore
			}
			return;
		}

		const items = this.#read();
		// Dedup: remove any identical entry already present (same pair).
		const filtered = items.filter((it) => !(it.value === value && it.original === original));
		filtered.unshift({ id: `${Date.now()}-${this.#seq++}`, original, value, ts: Date.now() });

		this.#persist(filtered.slice(0, ClipboardHistory.MAX_ITEMS));
		this.#renderIfOpen();
	}

	remove(id) {
		const items = this.#read().filter((it) => it.id !== id);
		this.#persist(items);
		this.render();
	}

	copyItem(id) {
		const item = this.#read().find((it) => it.id === id);
		if (!item) return;
		// Re-copy preserving the paragraph division (text/html + text/plain), the
		// same way the original copy was made.
		const paragraphs = item.value.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
		Promise.resolve(writeParagraphsToClipboard(paragraphs))
			.then(() => {
				if (window.toastr) window.toastr.success('Contenuto copiato');
			})
			.catch(() => {
				if (window.toastr) window.toastr.warning('Copia non disponibile in questo contesto');
			});
	}

	/**
	 * Escapes a string for safe interpolation into HTML, including both element
	 * content and double/single-quoted attribute values (e.g. title="...").
	 */
	#escape(str) {
		return String(str ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	/**
	 * Turns a (possibly HTML) clipboard payload into clean plain text, keeping
	 * the paragraph division as newlines so the panel shows it nicely (no <p> or
	 * other markup) and re-copy can rebuild the paragraphs.
	 */
	#normalizeValue(str) {
		let s = String(str ?? '');
		if (/<[a-z][\s\S]*>/i.test(s)) {
			s = s
				.replace(/<\/(?:p|div|h[1-6]|li)>/gi, '\n\n')
				.replace(/<br\s*\/?>/gi, '\n')
				.replace(/<[^>]+>/g, '')
				.replace(/&nbsp;/gi, ' ')
				.replace(/&amp;/gi, '&')
				.replace(/&lt;/gi, '<')
				.replace(/&gt;/gi, '>')
				.replace(/&quot;/gi, '"')
				.replace(/&#39;/gi, "'");
		}
		return s.replace(/\n{3,}/g, '\n\n').trim();
	}

	/** Escapes a string for HTML content and renders its line breaks as <br>. */
	#escapeMultiline(str) {
		return this.#escape(str).replace(/\n/g, '<br>');
	}

	#splitParagraphs(text) {
		return String(text ?? '')
			.split(/\n{2,}/)
			.map((p) => p.trim())
			.filter(Boolean);
	}

	#paragraphsHtml(text) {
		const paragraphs = this.#splitParagraphs(text);
		if (!paragraphs.length) {
			return `<p class="translation-modal-paragraph">${this.#escapeMultiline(text)}</p>`;
		}

		return paragraphs.map((p) => `<p class="translation-modal-paragraph">${this.#escapeMultiline(p)}</p>`).join('');
	}

	#markDropdownForReopen() {
		this.#reopenDropdownAfterOverlay = Boolean(this.menuEl?.classList.contains('show'));
	}

	#reopenDropdownIfNeeded() {
		if (!this.#reopenDropdownAfterOverlay) {
			return;
		}

		this.#reopenDropdownAfterOverlay = false;
		this.#skipNextRender = true;
		window.jQuery?.('#clipboardHistoryToggle')?.dropdown('show');
	}

	#openTextPopup(item) {
		if (!window.bootbox) {
			return;
		}

		const has_original = Boolean(item.original?.trim());
		const message = has_original
			? `<div class="translation-modal">
				<div class="translation-modal-panels">
					<div class="translation-modal-panel">
						<div class="translation-modal-label">Originale</div>
						<div class="translation-modal-text">${this.#paragraphsHtml(item.original)}</div>
					</div>
					<div class="translation-modal-panel">
						<div class="translation-modal-label">Traduzione</div>
						<div class="translation-modal-text">${this.#paragraphsHtml(item.value)}</div>
					</div>
				</div>
			</div>`
			: `<div class="translation-modal">
				<div class="translation-modal-text">${this.#paragraphsHtml(item.value)}</div>
			</div>`;

		this.#markDropdownForReopen();

		const dialog = window.bootbox.dialog({
			title: has_original ? 'Traduzione' : 'Testo copiato',
			size: has_original ? 'large' : undefined,
			message,
			buttons: {
				close: {
					label: 'Chiudi',
					className: 'btn-default',
				},
			},
		});

		if (window.jQuery && dialog) {
			window.jQuery(dialog).on('hidden.bs.modal', () => this.#reopenDropdownIfNeeded());
		}
	}

	/**
	 * Returns the image URL when the clipboard value is a single image link,
	 * otherwise null (multi-paragraph text or non-image URLs are ignored).
	 */
	#extractImageUrl(value) {
		const trimmed = String(value ?? '').trim();
		if (!trimmed || /\n/.test(trimmed) || !ResourceUtils.isImageUrl(trimmed)) {
			return null;
		}

		if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
			return trimmed;
		}

		return null;
	}

	#thumbnailHtml(url) {
		const href = this.#escape(NavigationUtils.getProxyStreamUrl(url));
		const title = this.#escape(url);

		return `<a class="popup-preview-image clipboard-history-thumb-link" href="${href}" title="${title}">
			<img class="clipboard-history-thumb" src="${href}" alt="" loading="lazy"
				onerror="this.closest('.clipboard-history-thumb-link')?.remove()" />
		</a>`;
	}

	#bindImagePopups() {
		const $ = window.jQuery;
		if (!$?.magnificPopup || !this.listEl || this.listEl.dataset.magnificBound) {
			return;
		}

		this.listEl.dataset.magnificBound = '1';
		$(this.listEl).magnificPopup({
			delegate: '.clipboard-history-thumb-link',
			type: 'image',
			closeOnContentClick: true,
			closeBtnInside: false,
			mainClass: 'mfp-with-zoom mfp-img-mobile',
			image: {
				verticalFit: true,
				titleSrc: (item) => item.el.attr('title') ?? '',
			},
			zoom: {
				enabled: true,
			},
			gallery: {
				enabled: true,
			},
			callbacks: {
				open: () => {
					this.#markDropdownForReopen();
				},
				afterClose: () => {
					this.#reopenDropdownIfNeeded();
				},
			},
		});
	}

	#renderIfOpen() {
		if (this.menuEl && this.menuEl.classList.contains('show')) {
			this.render();
		}
	}

	/** Renders the entries in the dropdown. */
	render() {
		if (!this.listEl) return;
		const items = this.#read();

		if (!items.length) {
			this.listEl.innerHTML = '<div class="clipboard-history-empty text-muted">Nessun elemento copiato</div>';
			return;
		}

		this.listEl.innerHTML = items
			.map((it) => {
				// title="..." stays single-line (full text); the visible preview keeps
				// the paragraph division as line breaks so it reads nicely.
				const full = this.#escape(it.value);
				const preview =
					it.value.length > ClipboardHistory.PREVIEW_LEN
						? this.#escapeMultiline(it.value.slice(0, ClipboardHistory.PREVIEW_LEN)) + '…'
						: this.#escapeMultiline(it.value);

				// For translations, show the source text small, on top.
				let originalHtml = '';
				if (it.original) {
					const originalFull = this.#escape(it.original);
					const originalPreview =
						it.original.length > ClipboardHistory.PREVIEW_LEN
							? this.#escapeMultiline(it.original.slice(0, ClipboardHistory.PREVIEW_LEN)) + '…'
							: this.#escapeMultiline(it.original);
					originalHtml = `<span class="clipboard-history-original" title="${originalFull}">${originalPreview}</span>`;
				}

				const imageUrl = this.#extractImageUrl(it.value);
				const thumbHtml = imageUrl ? this.#thumbnailHtml(imageUrl) : '';
				const contentClass = imageUrl ? 'clipboard-history-content' : 'clipboard-history-content clipboard-history-content--previewable';

				return `
					<div class="clipboard-history-item${imageUrl ? ' clipboard-history-item--image' : ''}" data-clip-id="${it.id}">
						<div class="clipboard-history-main">
							<div class="${contentClass}" data-action="preview" title="Apri anteprima">
								${originalHtml}
								<span class="clipboard-history-text" title="${full}">${preview}</span>
							</div>
							${thumbHtml}
						</div>
						<button type="button" class="btn btn-link clipboard-history-copy" data-action="copy"
							title="Copia">
							<i class="fa-regular fa-copy"></i>
						</button>
						<button type="button" class="btn btn-link clipboard-history-remove" data-action="remove"
							title="Rimuovi">
							<i class="fa-solid fa-xmark"></i>
						</button>
					</div>`;
			})
			.join('');
	}

	init() {
		this.menuEl = document.getElementById('clipboardHistoryMenu');
		this.listEl = document.getElementById('clipboardHistoryList');

		// Capture copies (Ctrl/Cmd+C and right-click → Copy) only when the selection
		// originates inside a [data-copy] element: we don't want every copy made
		// anywhere in the app to land in the history, just the ones the user is meant
		// to collect. Registered in every context (also iframes) so that copies made
		// inside the translation panel are captured too.
		document.addEventListener('copy', () => {
			try {
				const selection = window.getSelection();
				const text = selection?.toString();
				if (!text) return;
				let node = selection.anchorNode;
				if (node && node.nodeType !== Node.ELEMENT_NODE) node = node.parentNode;
				if (!(node instanceof Element) || !node.closest('[data-copy]')) return;
				this.add(text);
			} catch (_e) {
				// ignore any selection read errors
			}
		});

		// The top window owns the history: receive copies forwarded from iframes/popups
		// (see add()) and store/render them so the navbar dropdown updates without a reload.
		if (window.top === window.self) {
			window.addEventListener('message', (event) => {
				if (event.origin !== window.location.origin) return;
				const data = event.data;
				if (data && typeof data === 'object' && data.action === 'clipboard-add') {
					this.add({ original: data.original, value: data.value });
				}
			});
		}

		if (!this.listEl) return; // navbar not present on this page (e.g. inside an iframe)

		this.#bindImagePopups();

		// Render the list when the dropdown opens (Bootstrap event).
		const $menu = window.jQuery ? window.jQuery('#clipboard-history') : null;
		if ($menu) {
			$menu.on('shown.bs.dropdown', () => {
				if (this.#skipNextRender) {
					this.#skipNextRender = false;
					return;
				}

				this.render();
			});
		}

		// Delegate copy/remove/preview on the entry rows.
		this.listEl.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-action]');
			if (!btn) {
				return;
			}

			const row = btn.closest('.clipboard-history-item');
			const id = row?.getAttribute('data-clip-id');
			if (!id) {
				return;
			}

			const action = btn.getAttribute('data-action');
			if (action === 'copy') {
				this.copyItem(id);
			} else if (action === 'remove') {
				this.remove(id);
			} else if (action === 'preview') {
				const item = this.#read().find((it) => it.id === id);
				if (item) {
					this.#openTextPopup(item);
				}
			}
		});
	}
}
